import React, { useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { GlobalContext } from '../../../utils/GlobalContext';

// Metal color to CSS color mapping for swatches
const METAL_COLOR_MAP = {
    'White Gold': '#E8E8E8',
    'Yellow Gold': '#fae1a1',
    'Rose Gold': '#fddfbd',
    'PLATINUM': '#E5E4E2',
    'SILVER': '#C0C0C0',
};

// Swatch styles
const swatchStyles = {
    container: {
        position: 'relative',
        width: '100%',
    },
    selectedDisplay: {
        display: 'flex',
        alignItems: 'center',
        padding: '6px 12px',
        border: '1px solid #ced4da',
        borderRadius: '4px',
        backgroundColor: '#fff',
        cursor: 'pointer',
        minHeight: '38px',
    },
    selectedDisplayDisabled: {
        backgroundColor: '#e9ecef',
        cursor: 'not-allowed',
    },
    swatch: {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        border: '1px solid #ccc',
        marginRight: '10px',
        flexShrink: 0,
    },
    dropdown: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: '#fff',
        border: '1px solid #ced4da',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 1000,
        maxHeight: '200px',
        overflowY: 'auto',
    },
    option: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        cursor: 'pointer',
    },
    optionHover: {
        backgroundColor: '#f8f9fa',
    },
    optionSelected: {
        backgroundColor: '#e9ecef',
    },
    arrow: {
        marginLeft: 'auto',
        fontSize: '10px',
    },
};

export const CommonFilter = ({
  filterKey,
  selectedValue = '', // initial external value (may be empty)
  onChange,
  inputType = 'select',
  labelName = 'Select Option',
  disabled = false,
  requestOption = null,
  name = 'common_filter',
}) => {
    const { filterOption = {} } = useContext(GlobalContext);
    const { category } = useContext(GlobalContext);
    const labelNameOpt = labelName === 'Select Option' ? filterOption[filterKey]?.filter_name : labelName;

    // State for color swatch dropdown
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredOption, setHoveredOption] = useState(null);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getOptions = () => {
        let optionsString = filterOption[filterKey]?.filter_value || '';

        // Convert comma-separated string to array
        let options = optionsString.split(',').map(opt => opt.trim());

        // Optionally transform the array if requestOption is a function
        if (requestOption && typeof requestOption === 'function') {
            options = requestOption(options);
        }

        return options;
    };

    const options = getOptions();

    const getSwatchColor = (colorName) => {
        return METAL_COLOR_MAP[colorName] || '#ccc';
    };

    const handleColorSelect = (value) => {
        onChange(value);
        setIsOpen(false);
    };

    const renderColorSwatchSelect = () => (
        <div style={swatchStyles.container} ref={dropdownRef}>
            <div
                style={{
                    ...swatchStyles.selectedDisplay,
                    ...(disabled ? swatchStyles.selectedDisplayDisabled : {})
                }}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        !disabled && setIsOpen(!isOpen);
                    }
                }}
            >
                <span
                    style={{
                        ...swatchStyles.swatch,
                        backgroundColor: getSwatchColor(selectedValue),
                    }}
                />
                <span>{selectedValue || 'Select...'}</span>
                <span style={swatchStyles.arrow}>{isOpen ? '▲' : '▼'}</span>
            </div>
            {isOpen && (
                <div style={swatchStyles.dropdown} role="listbox">
                    {options.map((opt) => (
                        <div
                            key={opt}
                            style={{
                                ...swatchStyles.option,
                                ...(hoveredOption === opt ? swatchStyles.optionHover : {}),
                                ...(selectedValue === opt ? swatchStyles.optionSelected : {}),
                            }}
                            onClick={() => handleColorSelect(opt)}
                            onMouseEnter={() => setHoveredOption(opt)}
                            onMouseLeave={() => setHoveredOption(null)}
                            role="option"
                            aria-selected={selectedValue === opt}
                        >
                            <span
                                style={{
                                    ...swatchStyles.swatch,
                                    backgroundColor: getSwatchColor(opt),
                                }}
                            />
                            <span>{opt}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className="col-12 common-filter" style={{ padding: name == 'ring_type' || name == 'setting_type' ? '0px' : '5px' }}>
        {category == 'Ring Builder' ?(
            <label className="control-label" htmlFor={`${name}-input`}>
                <span style={{color:`red`}}>*</span>{labelNameOpt}:
            </label>
        ):(
            <label className="control-label" htmlFor={`${name}-input`}>
                {labelNameOpt}:
            </label>
        )}

        {inputType === 'colorSelect' ? (
            renderColorSwatchSelect()
        ) : inputType === 'select' ? (
            <select
            id={`${name}-input`}
            className="form-control"
            value={selectedValue}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            name={name}
            >
            {options.map((opt) => (
                <option key={opt} value={opt}>
                {opt}
                </option>
            ))}
            </select>
        ) : (
            <div>
            {options.map((opt) => (
                <div key={opt} className="form-check inline">
                <input
                    className="form-check-input"
                    type="radio"
                    name={name}
                    id={`${name}-${opt}`}
                    value={opt}
                    checked={selectedValue === opt}
                    onChange={(e) => onChange(opt)}
                    disabled={disabled}
                    aria-checked={selectedValue === opt}
                />
                <label className="form-check-label" htmlFor={`${name}-${opt}`}>
                    {opt}
                </label>
                </div>
            ))}
            </div>
        )}
        </div>
    );
};

CommonFilter.propTypes = {
  filterKey: PropTypes.string.isRequired,
  selectedValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  inputType: PropTypes.oneOf(['select', 'radio', 'colorSelect']),
  labelName: PropTypes.string,
  disabled: PropTypes.bool,
  requestOption: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  name: PropTypes.string,
};
